import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Amount, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, LoadingButton, StatusDot, TableSkeleton, Textarea } from '@redpanda/ui';
import { isAdmin, toIntlLocale } from '@redpanda/utils';
import type { Role } from '@redpanda/types';
import { getPeriodClosingById, updatePeriodClosingNotes } from '../services/api';
import { PeriodClosing, usePeriodClosingStoreBase } from '../stores/periodStore';
import { closingTone, toneOf } from '../utils/statusTone';
import { apiErrorMessage } from '../utils/apiError';

interface ClosingDetail extends PeriodClosing {
  closingEntries: Array<{ id: string; accountCode: string; accountName: string; debitAmount: number; creditAmount: number }>;
}

interface PeriodClosingDetailDialogProps {
  closing: PeriodClosing;
  userRole?: Role;
  onClose: () => void;
}

/** 結帳記錄詳情：基本資料（「標籤：值」）、各科目結帳金額、備註（管理員可改） */
const PeriodClosingDetailDialog: React.FC<PeriodClosingDetailDialogProps> = ({ closing, userRole, onClose }) => {
  const { t, i18n } = useTranslation();
  const locale = toIntlLocale(i18n.language);
  const [detail, setDetail] = useState<ClosingDetail | null>(null);
  const [notes, setNotes] = useState(closing.notes || '');
  const [saving, setSaving] = useState(false);
  const editable = isAdmin(userRole);

  useEffect(() => {
    getPeriodClosingById(closing.id)
      .then(response => setDetail(response.data))
      .catch(error => toast.error(apiErrorMessage(error, 'common.messages.fetchFailed')));
  }, [closing.id]);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await updatePeriodClosingNotes(closing.id, notes.trim());
      usePeriodClosingStoreBase.getState().updateItem(closing.id, { notes: notes.trim() });
      toast.success(t('periods.toast.noteUpdated'));
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const rows: Array<[string, React.ReactNode]> = [
    [t('periods.closings.type'), t(`periods.closingTypes.${closing.type}`)],
    [t('periods.closings.status'), <StatusDot tone={toneOf(closingTone, closing.status)} label={t(`periods.closingStatus.${closing.status}`)} />],
    [t('periods.closings.processedBy'), closing.processedByName === '系統' ? t('periods.system') : closing.processedByName || '—'],
    [t('periods.closings.completedAt'), when(closing.completedAt)],
  ];

  return (
    <DialogShell onClose={onClose} closeDisabled={saving} className="max-w-2xl">
      <DialogHeader
        title={t('periods.detail.title', { name: `${closing.period?.fiscalYear?.name ?? ''} ${closing.period?.name ?? ''}`.trim() })}
        onClose={onClose}
        closeDisabled={saving}
        closeLabel={t('common.actions.close')}
      />
      <div className="p-6 space-y-5">
        <dl className="divide-y divide-line border border-line rounded-lg text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[8rem_1fr] gap-3 px-4 py-2.5">
              <dt className="text-fg-secondary">{label}</dt>
              <dd className="text-fg">{value}</dd>
            </div>
          ))}
        </dl>

        <section>
          <h3 className="text-sm font-semibold text-fg mb-2">{t('periods.detail.entries')}</h3>
          {!detail ? (
            <TableSkeleton rows={4} columns={3} showHeader={false} />
          ) : detail.closingEntries.length === 0 ? (
            <p className="text-sm text-fg-secondary">{t('periods.detail.noEntries')}</p>
          ) : (
            <div className="border border-line rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800/60 text-[13px] font-semibold uppercase tracking-wider text-fg-secondary">
                  <tr>
                    <th className="py-2 px-4 text-left font-semibold">{t('reports.columns.accountName')}</th>
                    <th className="py-2 px-4 w-36 text-right font-semibold">{t('reports.columns.debit')}</th>
                    <th className="py-2 px-4 w-36 text-right font-semibold">{t('reports.columns.credit')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {detail.closingEntries.map(entry => (
                    <tr key={entry.id}>
                      <td className="py-2 px-4 text-fg"><span className="tabular-nums text-fg-secondary mr-2">{entry.accountCode}</span>{entry.accountName}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{entry.debitAmount ? <Amount value={entry.debitAmount} /> : null}</td>
                      <td className="py-2 px-4 text-right tabular-nums">{entry.creditAmount ? <Amount value={entry.creditAmount} /> : null}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold text-fg border-t-2 border-line">
                    <td className="py-2 px-4">{t('reports.total')}</td>
                    <td className="py-2 px-4 text-right tabular-nums"><Amount value={detail.totalDebits} /></td>
                    <td className="py-2 px-4 text-right tabular-nums"><Amount value={detail.totalCredits} /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section>
          <label htmlFor="closingDetailNotes" className="block text-sm font-semibold text-fg mb-2">{t('periods.closingDialog.notes')}</label>
          {editable ? (
            <Textarea id="closingDetailNotes" rows={3} maxLength={1000} value={notes} onChange={event => setNotes(event.target.value)} />
          ) : (
            <p className="text-sm text-fg-secondary whitespace-pre-wrap">{closing.notes || '—'}</p>
          )}
        </section>
      </div>
      <DialogFooter>
        <button type="button" onClick={onClose} disabled={saving} className={dialogButtonClass.cancel}>
          {editable ? t('common.actions.cancel') : t('common.actions.close')}
        </button>
        {editable && (
          <LoadingButton type="button" loading={saving} disabled={notes.trim() === (closing.notes || '')} onClick={saveNotes} className={dialogButtonClass.primary}>
            {t('common.actions.save')}
          </LoadingButton>
        )}
      </DialogFooter>
    </DialogShell>
  );
};

export default PeriodClosingDetailDialog;
